import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Bookmark, Repeat2, Flag, Star, Clock, Package, FileDown, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, money, SERVICE_TYPE_LABEL, timeAgo, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/layout/AppShell";
import { SnapshotView } from "@/components/TarotCard";
import { Media, mediaKind, absUrl } from "@/components/Uploader";

export const MemberBadge = () => <span className="mono text-[8px] uppercase tracking-wider text-[#C5A059] border border-[#C5A059] px-1 py-px rounded-sm" data-testid="member-badge">Circle</span>;
export const TypeBadge = ({ type, className = "" }: { type?: string | null; className?: string }) => type ? <span className={`mono text-[8px] uppercase tracking-wider text-[#581C25] border border-[#581C25]/40 bg-[#581C25]/5 px-1 py-px rounded-sm whitespace-nowrap ${className}`} data-testid="type-badge">{type}</span> : null;

export function ReaderCard({ r }: { r: any }) {
  return (
    <Link to={`/@${r.username}`} className="card-tactile p-5 flex gap-4 hover:-translate-y-0.5 transition-transform" data-testid={`reader-card-${r.username}`}>
      <Avatar u={r} size={56} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="serif text-xl leading-tight truncate">{r.name}</p>
          <TypeBadge type={r.creator_type} />
          {r.verified && <span className="mono text-[9px] text-[#C5A059] border border-[#C5A059] px-1">VERIFIED</span>}
        </div>
        <p className="mono text-[11px] text-[#5E5C56]">@{r.username} · <Star size={10} className="inline -mt-0.5" /> {r.rating || "—"} ({r.rating_count || 0})</p>
        <p className="text-sm text-[#2C2A28] mt-2 line-clamp-2">{r.bio}</p>
        <div className="flex flex-wrap gap-1.5 mt-3">{(r.specialties || []).slice(0, 3).map((s: string) => <span key={s} className="chip !py-0.5 !text-[10px] !cursor-default">{s}</span>)}</div>
      </div>
    </Link>
  );
}

export function ServiceCard({ s, showReader = true }: { s: any; showReader?: boolean }) {
  return (
    <Link to={`/services/${s.id}`} className="card-tactile p-5 flex flex-col gap-3 hover:-translate-y-0.5 transition-transform" data-testid={`service-card-${s.id}`}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{SERVICE_TYPE_LABEL[s.type] || s.type}</span>
        <span className="mono text-sm font-semibold">{money(s.price)}</span>
      </div>
      <p className="serif text-xl leading-tight">{s.title}</p>
      <p className="text-sm text-[#5E5C56] line-clamp-2 flex-1">{s.description}</p>
      <div className="flex items-center justify-between text-xs text-[#5E5C56]">
        {showReader && s.reader ? <span className="flex items-center gap-2"><Avatar u={s.reader} size={20} />{s.reader.name}</span> : <span />}
        <span className="flex items-center gap-1 mono"><Clock size={11} />{s.type === "chat" ? `${s.duration_minutes} min` : `${s.delivery_days}d delivery`}</span>
      </div>
    </Link>
  );
}

export function ProductCard({ p }: { p: any }) {
  return (
    <Link to={`/products/${p.id}`} className="group card-tactile overflow-hidden flex flex-col hover:-translate-y-0.5 transition-transform" data-testid={`product-card-${p.id}`}>
      <div className="aspect-[4/3] bg-[#1A1917] overflow-hidden">
        {p.image_url ? <img src={absUrl(p.image_url)} alt={p.title} className="h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500" /> :
          <div className="h-full w-full flex items-center justify-center text-[#C5A059]">{p.kind === "digital" ? <FileDown /> : <Package />}</div>}
      </div>
      <div className="p-4 flex flex-col gap-1.5">
        <div className="flex items-center justify-between"><span className="eyebrow">{p.kind}{p.category && p.category !== "Other" ? ` · ${p.category}` : ""}</span><span className="mono text-sm font-semibold">{money(p.price)}</span></div>
        <p className="serif text-lg leading-tight">{p.title}</p>
        {p.reader && <p className="text-xs text-[#5E5C56]">by {p.reader.name}</p>}
      </div>
    </Link>
  );
}

export function PostCard({ post, onChange }: { post: any; onChange?: (p: any) => void }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(post);
  const guard = () => { if (!user) { nav("/login"); return false; } return true; };
  const act = async (kind: "like" | "save" | "repost") => {
    if (!guard()) return;
    try {
      const { data } = await api.post(`/posts/${p.id}/${kind}`);
      if (kind === "like") setP({ ...p, liked: data.liked, likes_count: p.likes_count + (data.liked ? 1 : -1) });
      if (kind === "save") { setP({ ...p, saved: data.saved }); toast(data.saved ? "Saved to your collection" : "Removed from saved"); }
      if (kind === "repost") { toast.success("Reposted to your channel"); setP({ ...p, reposts_count: p.reposts_count + 1 }); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const report = async () => {
    if (!guard()) return;
    const reason = window.prompt("Why are you reporting this post?");
    if (!reason) return;
    await api.post(`/posts/${p.id}/report`, { body: reason });
    toast("Report sent to moderation");
  };
  const a = p.author || {};
  return (
    <article className="card-tactile p-5 rise" data-testid={`post-card-${p.id}`}>
      <div className="flex items-center gap-3">
        <Link to={`/@${a.username}`}><Avatar u={a} size={36} /></Link>
        <div className="flex-1 min-w-0">
          <Link to={`/@${a.username}`} className="serif text-lg leading-none hover:underline">{a.name}</Link> <TypeBadge type={a.creator_type} className="ml-1 align-middle" />
          <p className="mono text-[10px] text-[#5E5C56]">@{a.username} · {timeAgo(p.created_at)} · {p.type.replace("_", " ")}{p.members_only ? " · members" : ""}</p>
        </div>
        {p.repost_of && <span className="mono text-[9px] text-[#5E5C56] flex items-center gap-1"><Repeat2 size={11} /> via @{p.repost_author?.username}</span>}
      </div>
      <Link to={`/posts/${p.id}`} className="block mt-4">
        {p.title && <h3 className="text-2xl font-semibold leading-tight mb-1.5">{p.title}</h3>}
        {p.body && <p className={`text-[15px] text-[#2C2A28] leading-relaxed whitespace-pre-wrap ${p.type === "article" ? "line-clamp-4" : ""}`}>{p.body}</p>}
      </Link>
      {p.media_urls?.length > 0 && (
        <div className={`mt-4 grid gap-2 ${p.media_urls.length > 1 && p.media_urls.every((u: string) => mediaKind(u) === "image") ? "grid-cols-2" : ""}`}>
          {p.media_urls.slice(0, 6).map((u: string, i: number) => <Media key={i} url={u} className={mediaKind(u) === "image" ? "aspect-[4/3]" : ""} />)}
        </div>
      )}
      {p.locked && (
        <Link to={`/@${a.username}?tab=circle`} className="mt-4 flex items-center gap-3 rounded-md border border-[#C5A059] bg-[#F6F4EE] p-4 hover:bg-[#EFECE4] transition-colors" data-testid={`locked-post-${p.id}`}>
          <Lock size={18} className="text-[#581C25]" />
          <div><p className="text-sm font-medium">Members-only post</p><p className="text-xs text-[#5E5C56]">Join {a.name}'s Circle to unlock this and every member post.</p></div>
        </Link>
      )}
      {p.snapshot && <div className="mt-4"><SnapshotView snap={p.snapshot} compact /></div>}
      {p.service && <div className="mt-4"><ServiceCard s={{ ...p.service, reader: a }} showReader={false} /></div>}
      {p.product && <div className="mt-4 max-w-xs"><ProductCard p={{ ...p.product, reader: a }} /></div>}
      {p.tags?.length > 0 && <div className="flex flex-wrap gap-1.5 mt-4">{p.tags.map((t: string) => <span key={t} className="mono text-[10px] text-[#581C25]">#{t}</span>)}</div>}
      <div className="hairline mt-4 pt-3 flex items-center gap-5 text-[#5E5C56] text-xs">
        <button onClick={() => act("like")} className={`flex items-center gap-1.5 transition-colors ${p.liked ? "text-[#581C25]" : "hover:text-[#1A1917]"}`} data-testid={`like-btn-${p.id}`}>
          <Heart size={16} fill={p.liked ? "currentColor" : "none"} /><span className="mono">{p.likes_count}</span></button>
        <Link to={`/posts/${p.id}`} className="flex items-center gap-1.5 hover:text-[#1A1917]" data-testid={`comment-btn-${p.id}`}><MessageCircle size={16} /><span className="mono">{p.comments_count}</span></Link>
        <button onClick={() => act("repost")} className="flex items-center gap-1.5 hover:text-[#1A1917]" data-testid={`repost-btn-${p.id}`}><Repeat2 size={16} /><span className="mono">{p.reposts_count}</span></button>
        <button onClick={() => act("save")} className={`ml-auto ${p.saved ? "text-[#581C25]" : "hover:text-[#1A1917]"}`} data-testid={`save-btn-${p.id}`}><Bookmark size={16} fill={p.saved ? "currentColor" : "none"} /></button>
        <button onClick={report} className="hover:text-[#1A1917]" data-testid={`report-btn-${p.id}`}><Flag size={14} /></button>
      </div>
    </article>
  );
}
